import React, { useEffect, useRef, useState } from "react";
import PositionCell from "./PositionCell";
import { DisplayFields, PositionData } from "./types";
import { RowKey, UIPair } from "../../helpers/useHoldingsHistoryHelper";
import "../../styles/scrollbar.css";
import { AumPill, TickerPill, AumTier } from "./InstitutionSelector";

const typeLabels: Record<string, string> = {
    SHARE: "Share",
    CALL: "Call / Long",
    PUT: "Put / Short",
};

const prettyTicker = (t?: string | null): string => {
    if (!t) return "";
    const base = t.includes("_") ? t.split("_")[0] : t;
    if (!base) return "";
    return base.toLowerCase() === "null" ? "" : base;
};

const yqToNum = (s: string): number => {
    const m = /(\d{4})\s*[- ]?\s*Q?([1-4])/i.exec(s);
    return m ? Number(m[1]) * 10 + Number(m[2]) : NaN;
};
const yqGte = (a: string, b: string) => yqToNum(a) >= yqToNum(b);
const NEW_FLOOR_YQ = "2013Q3";

type ColumnMetaByPeriod = Record<
    string,
    {
        period: string;
        snapshotId: number;
        baseReportId: number | null;
        report?: {
            id: number;
            filingUri: string;
            isReportedTableValueTotalSuspect: boolean;
            isProcessedTableValueTotalSuspect: boolean;
            processedInfoTableValueUnitEnum?: string;
            reportedInfoTableValueUnitEnum?: string;
        };
    }
>;

type InstitutionMeta = { name: string; ticker: string | null; tier: AumTier };

export default function TableView({
                                      allYearQuarters,
                                      pairs,
                                      rowPivot,
                                      totalsByYQ,
                                      totalValueInNewestQuarter,
                                      showPositionShare,
                                      displayFields,
                                      institutionMap,
                                      institutionMetaMap,
                                      mode,
                                      aggregatePositions,
                                      columnMetaByPeriod,
                                  }: {
    allYearQuarters: string[];
    pairs: UIPair[];
    rowPivot: Record<RowKey, Record<string, PositionData>>;
    totalsByYQ?: Record<string, number>;
    totalValueInNewestQuarter?: number;
    showPositionShare: boolean;
    displayFields: DisplayFields;
    institutionMap: Record<number, string>;
    institutionMetaMap: Record<number, InstitutionMeta>;
    mode: "institution" | "ticker";
    aggregatePositions: boolean;
    columnMetaByPeriod: ColumnMetaByPeriod;
}) {
    // Wider first column for names + pills, narrower Type column
    const leftWidth = mode === "ticker" ? 440 : 300;
    const TYPE_COL_W = 100; // keeps "Call / Long" and "Put / Short" without wrapping
    const QUARTER_COL_W = 300;
    const contentWidth = leftWidth + TYPE_COL_W + QUARTER_COL_W * Math.max(0, allYearQuarters.length);

    const mainRef = useRef<HTMLDivElement>(null);
    const topRef = useRef<HTMLDivElement>(null);
    const syncing = useRef(false);

    const [needsScroll, setNeedsScroll] = useState(false);
    const [hasLeftOverflow, setHasLeftOverflow] = useState(false);
    const [hasRightOverflow, setHasRightOverflow] = useState(false);

    const updateShadowsFrom = (el: HTMLElement) => {
        const l = el.scrollLeft;
        const max = el.scrollWidth - el.clientWidth;
        setHasLeftOverflow(l > 0);
        setHasRightOverflow(l < max - 1);
    };

    useEffect(() => {
        const el = mainRef.current;
        if (!el) return;
        const measure = () => {
            setNeedsScroll(el.scrollWidth > el.clientWidth + 1);
            updateShadowsFrom(el);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, [contentWidth, allYearQuarters.length]);

    useEffect(() => {
        if (needsScroll && mainRef.current && topRef.current) {
            topRef.current.scrollLeft = mainRef.current.scrollLeft;
        }
    }, [needsScroll, contentWidth]);

    const onTopScroll: React.UIEventHandler<HTMLDivElement> = () => {
        if (syncing.current) return;
        const top = topRef.current;
        const main = mainRef.current;
        if (!top || !main) return;
        syncing.current = true;
        main.scrollLeft = top.scrollLeft;
        updateShadowsFrom(main);
        syncing.current = false;
    };

    const onMainScroll: React.UIEventHandler<HTMLDivElement> = (e) => {
        const el = e.currentTarget;
        updateShadowsFrom(el);
        if (!needsScroll) return;
        const top = topRef.current;
        if (!top || syncing.current) return;
        syncing.current = true;
        top.scrollLeft = el.scrollLeft;
        syncing.current = false;
    };

    const SuspectBadge = ({ kind, title }: { kind: "R" | "P"; title: string }) => (
        <span
            title={title}
            style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 16,
                height: 16,
                borderRadius: 999,
                fontSize: 10,
                fontWeight: 800,
                marginLeft: 6,
                background: kind === "R" ? "#f87171" : "#fbbf24",
                color: "#1b0f0f",
                border: "1px solid rgba(0,0,0,0.25)",
            }}
        >
      {kind}
    </span>
    );

    const UnitChip = ({ unit }: { unit?: string }) => {
        if (!unit) return null;
        return (
            <span
                title={`Value unit: ${unit}`}
                style={{
                    marginLeft: 8,
                    padding: "1px 6px",
                    borderRadius: 6,
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    fontSize: 10.5,
                    fontWeight: 700,
                }}
            >
        {unit}
      </span>
        );
    };

    return (
        <div style={{ position: "relative", width: "100%" }}>
            {needsScroll && (
                <div
                    ref={topRef}
                    className="scrollbarDark"
                    onScroll={onTopScroll}
                    style={{
                        width: "100%",
                        overflowX: "auto",
                        overflowY: "hidden",
                        height: 12,
                        margin: "6px 0 2px",
                        scrollbarWidth: "thin",
                        WebkitOverflowScrolling: "touch",
                        scrollbarGutter: "stable",
                        borderRadius: 8,
                    }}
                    aria-hidden
                >
                    <div style={{ width: contentWidth, height: 1 }} />
                </div>
            )}

            {hasLeftOverflow && (
                <div
                    style={{
                        position: "absolute",
                        left: 0,
                        top: needsScroll ? 20 : 14,
                        bottom: 0,
                        width: 24,
                        pointerEvents: "none",
                        background: "linear-gradient(90deg, rgba(20,20,20,1) 0%, rgba(20,20,20,0.75) 40%, rgba(20,20,20,0) 100%)",
                        zIndex: 2,
                    }}
                />
            )}
            {hasRightOverflow && (
                <div
                    style={{
                        position: "absolute",
                        right: 0,
                        top: needsScroll ? 20 : 14,
                        bottom: 0,
                        width: 24,
                        pointerEvents: "none",
                        background: "linear-gradient(270deg, rgba(20,20,20,1) 0%, rgba(20,20,20,0.75) 40%, rgba(20,20,20,0) 100%)",
                        zIndex: 2,
                    }}
                />
            )}

            <div
                ref={mainRef}
                className="scrollbarDark"
                onScroll={onMainScroll}
                style={{
                    position: "relative",
                    width: "100%",
                    overflowX: "auto",
                    overflowY: "hidden",
                    paddingBottom: 8,
                    scrollbarWidth: "thin",
                    WebkitOverflowScrolling: "touch",
                    scrollbarGutter: "stable",
                }}
            >
                <table
                    style={{
                        width: `${contentWidth}px`,
                        tableLayout: "fixed",
                        borderCollapse: "separate",
                        borderSpacing: 0,
                        backgroundColor: "#141414",
                        color: "#fff",
                    }}
                >
                    <thead>
                    <tr style={{ position: "sticky" as const, top: 0, zIndex: 3, background: "#232323" }}>
                        <th
                            style={{
                                position: "sticky",
                                left: 0,
                                zIndex: 5,
                                background: "#232323",
                                padding: "12px 12px",
                                textAlign: "left",
                                fontWeight: 900,
                                fontSize: 13,
                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                                width: leftWidth,
                                minWidth: leftWidth,
                                boxShadow: "inset -1px 0 0 rgba(255,255,255,0.06)",
                                willChange: "transform",
                                transform: "translateZ(0)",
                            }}
                        >
                            {mode === "ticker" ? "Institution" : "Holding"}
                        </th>

                        <th
                            style={{
                                position: "sticky",
                                left: leftWidth,
                                zIndex: 4,
                                width: TYPE_COL_W,
                                minWidth: TYPE_COL_W,
                                padding: "12px 10px",
                                textAlign: "left",
                                fontWeight: 900,
                                fontSize: 13,
                                background: "#232323",
                                borderBottom: "1px solid rgba(255,255,255,0.08)",
                                boxShadow: "inset -1px 0 0 rgba(255,255,255,0.06)",
                                willChange: "transform",
                                transform: "translateZ(0)",
                            }}
                        >
                            Type
                        </th>

                        {allYearQuarters.map((yq) => {
                            const meta = columnMetaByPeriod[yq];
                            const hasR = !!meta?.report?.isReportedTableValueTotalSuspect;
                            const hasP = !!meta?.report?.isProcessedTableValueTotalSuspect;
                            const unit =
                                meta?.report?.processedInfoTableValueUnitEnum ||
                                meta?.report?.reportedInfoTableValueUnitEnum;

                            return (
                                <th
                                    key={yq}
                                    style={{
                                        width: QUARTER_COL_W,
                                        minWidth: QUARTER_COL_W,
                                        padding: "12px 10px",
                                        textAlign: "left",
                                        fontWeight: 900,
                                        fontSize: 13,
                                        background: "#232323",
                                        borderBottom: "1px solid rgba(255,255,255,0.08)",
                                    }}
                                >
                                    <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                        <span>{yq}</span>
                                        {unit && <UnitChip unit={unit} />}
                                        {hasR && <SuspectBadge kind="R" title="Header total marked suspect" />}
                                        {hasP && <SuspectBadge kind="P" title="Processed total marked suspect" />}
                                    </div>
                                </th>
                            );
                        })}
                    </tr>
                    </thead>

                    <tbody>
                    {pairs.map((row) => {
                        const cells = rowPivot[row.rowKey] ?? {};
                        const firstPos = cells[allYearQuarters[0]] || Object.values(cells)[0];

                        const displayTicker = prettyTicker(row.ticker);
                        const title = firstPos?.title || "";
                        const cusip = (firstPos as any)?.cusip;

                        const leftTop =
                            mode === "ticker"
                                ? aggregatePositions
                                    ? "All Institutions"
                                    : row.institutionId
                                        ? institutionMap[row.institutionId] ?? `Institution ${row.institutionId}`
                                        : "Institution"
                                : displayTicker || "—";

                        const meta: InstitutionMeta | undefined =
                            mode === "ticker" && !aggregatePositions && row.institutionId
                                ? institutionMetaMap[row.institutionId]
                                : undefined;

                        const showSubtitle = mode !== "ticker"; // hide company + cusip in ticker view

                        return (
                            <tr key={row.rowKey} style={{ borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
                                <td
                                    style={{
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 3,
                                        backgroundColor: "#1c1c1c",
                                        width: leftWidth,
                                        minWidth: leftWidth,
                                        padding: "10px 12px",
                                        lineHeight: 1.2,
                                        wordBreak: "break-word",
                                        whiteSpace: "normal",
                                        borderRight: "1px solid rgba(255,255,255,0.06)",
                                        willChange: "transform",
                                        transform: "translateZ(0)",
                                        verticalAlign: "middle",
                                    }}
                                >
                                    {/* First line: name (single line + ellipsis) + pills */}
                                    <div
                                        style={{
                                            display: "grid",
                                            gridTemplateColumns: meta ? "minmax(0,1fr) max-content" : "1fr",
                                            alignItems: "center",
                                            columnGap: 8,
                                            marginBottom: showSubtitle ? 4 : 0,
                                        }}
                                    >
                                        <div
                                            // single line with ellipsis
                                            style={{
                                                fontSize: 14,
                                                fontWeight: 900,
                                                minWidth: 0,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap",
                                            }}
                                            title={leftTop} // hover to see full name
                                        >
                                            {leftTop}
                                        </div>

                                        {meta && (
                                            <div style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                                                {meta.ticker ? <TickerPill ticker={meta.ticker} /> : null}
                                                <AumPill tier={meta.tier} />
                                            </div>
                                        )}
                                    </div>

                                    {/* Subtitle only for institution mode */}
                                    {showSubtitle && title && (
                                        <div style={{ fontSize: 12.5, color: "#e8e8e8" }}>{title}</div>
                                    )}
                                    {showSubtitle && cusip && (
                                        <div style={{ fontSize: 11.5, color: "#bdbdbd", marginTop: 2 }}>{cusip}</div>
                                    )}
                                </td>

                                <td
                                    style={{
                                        position: "sticky",
                                        left: leftWidth,
                                        zIndex: 2,
                                        width: TYPE_COL_W,
                                        minWidth: TYPE_COL_W,
                                        fontSize: 12.5,
                                        color:
                                            row.type === "SHARE" ? "#fff" : row.type === "CALL" ? "#34d399" : "#f87171",
                                        fontWeight: row.type === "SHARE" ? ("bold" as const) : ("normal" as const),
                                        padding: "10px",
                                        backgroundColor: "#1c1c1c",
                                        borderRight: "1px solid rgba(255,255,255,0.06)",
                                        willChange: "transform",
                                        transform: "translateZ(0)",
                                        verticalAlign: "middle",
                                    }}
                                >
                                    {typeLabels[row.type] ?? row.type}
                                </td>

                                {allYearQuarters.map((yq, i) => {
                                    const pos = cells[yq];
                                    const prevPos =
                                        i < allYearQuarters.length - 1 ? cells[allYearQuarters[i + 1]] : undefined;

                                    const columnTotal =
                                        totalsByYQ && totalsByYQ[yq] !== undefined
                                            ? totalsByYQ[yq]
                                            : allYearQuarters[0] === yq
                                                ? totalValueInNewestQuarter ?? 0
                                                : 0;

                                    const showNew = !!pos?.isNew && yqGte(yq, NEW_FLOOR_YQ);
                                    const showExited = !!pos?.isExited;
                                    const amendFlag = pos?.amendFlag;

                                    return (
                                        <td
                                            key={yq}
                                            style={{
                                                width: QUARTER_COL_W,
                                                minWidth: QUARTER_COL_W,
                                                backgroundColor: "#141414",
                                                color: "#FFF",
                                                verticalAlign: "top",
                                                textAlign: "left",
                                                border: 0,
                                                padding: 6,
                                                height: "100%",
                                            }}
                                        >
                                            <div style={{ height: "100%" }}>
                                                <PositionCell
                                                    pos={pos}
                                                    prevPos={prevPos}
                                                    showPositionShare={showPositionShare}
                                                    displayFields={displayFields}
                                                    columnTotal={columnTotal}
                                                    fill
                                                    isAggregatedRow={aggregatePositions}
                                                    isNew={showNew}
                                                    isExited={showExited}
                                                    amendFlag={amendFlag}
                                                />
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                        );
                    })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
